// 通話パネル（発信中/通話中/ホールド/字幕/操作列）— AI-WF265 / 要望#213（設計v2 §2.5）。
// house style: UIライブラリなし・inline style を末尾 S に集約。

import { useEffect, useRef, useState } from 'react';
import { useCallStore, hangup, toggleMute, toggleHold, setAutoForward, closeCallPanel } from './call-store';

function fmtDuration(ms: number): string {
  const s = Math.floor(ms / 1000);
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

function PhaseBadge({ phase }: { phase: string }) {
  const map: Record<string, { label: string; bg: string }> = {
    dialing: { label: '発信中…', bg: '#f59e0b' },
    active: { label: '通話中', bg: '#16a34a' },
    hold: { label: 'ホールド中', bg: '#0ea5e9' },
    ending: { label: '切断中…', bg: '#94a3b8' },
    ended: { label: '終了', bg: '#64748b' },
  };
  const m = map[phase] ?? { label: phase, bg: '#64748b' };
  return <span style={{ ...S.badge, background: m.bg }}>{m.label}</span>;
}

function LevelMeter({ level, label }: { level: number; label: string }) {
  const pct = Math.min(100, Math.round(level * 300)); // RMS(0..~0.3) を見やすく増幅
  return (
    <span style={S.meterWrap} title={`${label} レベル`}>
      <span style={S.meterLabel}>{label}</span>
      <span style={S.meterTrack}>
        <span style={{ ...S.meterFill, width: `${pct}%` }} data-testid={`call-level-${label}`} data-level={level.toFixed(3)} />
      </span>
    </span>
  );
}

export function CallPanel() {
  const st = useCallStore();
  const [, forceTick] = useState(0);
  const logRef = useRef<HTMLDivElement | null>(null);

  // 経過タイマー表示（1秒ごと再描画）
  useEffect(() => {
    if (st.phase !== 'active' && st.phase !== 'hold') return;
    const t = window.setInterval(() => forceTick((n) => n + 1), 1000);
    return () => window.clearInterval(t);
  }, [st.phase]);

  // 字幕の自動スクロール
  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [st.lines.length, st.partial, st.aiText]);

  if (st.phase === 'idle') return null;

  return (
    <div style={S.panel} data-testid="call-panel" data-phase={st.phase}>
      <div style={S.head}>
        <span style={S.title}>📞 AI 通話</span>
        <PhaseBadge phase={st.phase} />
        {st.startedAt != null && (st.phase === 'active' || st.phase === 'hold') && (
          <span style={S.timer} data-testid="call-timer">{fmtDuration(Date.now() - st.startedAt)}</span>
        )}
        {st.conversationId && (
          <span style={S.conv} title={st.conversationId}>{st.conversationId.slice(0, 18)}…</span>
        )}
      </div>

      {st.phase === 'ended' ? (
        <div style={S.endedBox}>
          <div style={{ color: '#475569' }}>{st.endedReason ?? '終了しました'}</div>
          {st.error && <div style={S.error} data-testid="call-error">{st.error}</div>}
          <button className="btn-ghost" type="button" onClick={closeCallPanel} data-testid="call-close">
            閉じる
          </button>
        </div>
      ) : (
        <>
          <div ref={logRef} style={S.subtitles} data-testid="call-subtitles">
            {st.lines.map((l) => (
              <div key={l.id} style={l.who === 'operator' ? S.lineOp : S.lineAi} data-testid={`call-subtitle-${l.who === 'operator' ? 'op' : 'ai'}`}>
                <span style={S.lineWho}>{l.who === 'operator' ? 'あなた' : 'AI'}</span>
                <span>{l.text}</span>
                {l.sent === 'pending' && <span style={S.sentMark} title="konecco へ送信中">…</span>}
                {l.sent === 'delivered' && <span style={S.sentMark} title="konecco へ配達済み">✓</span>}
                {l.sent === 'held' && <span style={S.sentMark} title="AI 応答中のため保留（応答後に送信）">⏸</span>}
              </div>
            ))}
            {st.partial && (
              <div style={{ ...S.lineOp, ...S.partial }} data-testid="call-subtitle-partial">
                <span style={S.lineWho}>あなた</span>
                <span>{st.partial}</span>
              </div>
            )}
            {st.aiStreaming && st.aiText !== '' && (
              <div style={S.lineAi} data-testid="call-subtitle-ai-streaming">
                <span style={S.lineWho}>AI</span>
                <span>{st.aiText}</span>
                <span style={S.caret}>▍</span>
              </div>
            )}
            {st.lines.length === 0 && !st.partial && !st.aiText && (
              <div style={S.hint}>
                {st.phase === 'dialing' ? '接続しています…' : 'マイクに向かって話すと字幕が出ます'}
              </div>
            )}
          </div>

          {st.progress && (
            <div style={S.progress} data-testid="call-progress">⏳ {st.progress}</div>
          )}
          {st.error && <div style={S.error} data-testid="call-error">{st.error}</div>}

          <div style={S.meters}>
            <LevelMeter level={st.micLevel} label="mic" />
            <LevelMeter level={st.spkLevel} label="spk" />
          </div>

          <div style={S.controls}>
            <button
              className="btn-ghost"
              type="button"
              onClick={toggleMute}
              disabled={st.phase === 'dialing' || st.phase === 'ending'}
              style={st.muted ? S.btnOn : undefined}
              data-testid="call-mute"
              title={st.muted ? 'ミュート解除' : 'ミュート（マイク停止）'}
            >
              {st.muted ? '🎙️✕' : '🎙️'}
            </button>
            <button
              className="btn-ghost"
              type="button"
              onClick={toggleHold}
              disabled={st.phase === 'dialing' || st.phase === 'ending'}
              style={st.phase === 'hold' ? S.btnOn : undefined}
              data-testid="call-hold"
              title={st.phase === 'hold' ? 'ホールド解除' : 'ホールド（上り/下りを一時停止）'}
            >
              ⏸
            </button>
            <button
              className="btn-danger"
              type="button"
              onClick={() => void hangup()}
              disabled={st.phase === 'ending'}
              data-testid="call-hangup"
              title="切断"
            >
              🔴 切断
            </button>
            <label style={S.fwdToggle} title="STT 確定テキストを konecco（AI）へ自動送信する（暫定ターンテイキング。#212 の VS 側結線後は OFF）">
              <input
                type="checkbox"
                checked={st.autoForward}
                onChange={(e) => setAutoForward(e.target.checked)}
                data-testid="call-autoforward"
              />
              AIへ自動送信
            </label>
          </div>
        </>
      )}
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  panel: {
    position: 'fixed', right: 16, bottom: 16, width: 380, zIndex: 60,
    background: '#fff', border: '1px solid #d3d7dd', borderRadius: 12,
    boxShadow: '0 8px 30px rgba(15,23,42,0.18)', padding: 12,
    display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13,
  },
  head: { display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 },
  title: { fontWeight: 700 },
  badge: { color: '#fff', borderRadius: 999, padding: '2px 8px', fontSize: 11, fontWeight: 700 },
  timer: { fontVariantNumeric: 'tabular-nums', color: '#475569' },
  conv: { marginLeft: 'auto', color: '#94a3b8', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 120 },
  subtitles: {
    maxHeight: 220, minHeight: 90, overflowY: 'auto', background: '#f8fafc',
    border: '1px solid #e2e8f0', borderRadius: 8, padding: 8,
    display: 'flex', flexDirection: 'column', gap: 4,
  },
  lineOp: { alignSelf: 'flex-end', background: '#dbeafe', borderRadius: 8, padding: '4px 8px', maxWidth: '90%' },
  lineAi: { alignSelf: 'flex-start', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '4px 8px', maxWidth: '90%' },
  lineWho: { fontSize: 10, fontWeight: 700, color: '#64748b', marginRight: 6 },
  partial: { opacity: 0.6, fontStyle: 'italic' },
  caret: { animation: 'koneccoBlink 1s step-end infinite', marginLeft: 2, color: '#2563eb' },
  sentMark: { marginLeft: 6, fontSize: 10, color: '#16a34a' },
  hint: { color: '#94a3b8', textAlign: 'center', margin: 'auto' },
  progress: { color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '3px 8px', fontSize: 12 },
  error: { color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '3px 8px', fontSize: 12 },
  meters: { display: 'flex', gap: 12 },
  meterWrap: { display: 'flex', alignItems: 'center', gap: 4, flex: 1 },
  meterLabel: { fontSize: 10, color: '#64748b', width: 24 },
  meterTrack: { flex: 1, height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' },
  meterFill: { display: 'block', height: '100%', background: '#22c55e', transition: 'width 0.1s linear' },
  controls: { display: 'flex', alignItems: 'center', gap: 8 },
  btnOn: { background: '#e0e7ff', borderRadius: 6 },
  fwdToggle: { marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#475569', cursor: 'pointer' },
  endedBox: { display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' },
};
